import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MessageType, monitoring } from '@caslink/common-client';
import { ViewModelUpdateInfo } from '@caslink/common-client/ng-app/alerts/typings/ViewModelUpdateInfo';
import { CardStatusUpdateInfo } from '@caslink/common-client/ng-app/alerts/typings/CardStatusUpdateInfo';
import { CommonBroadcastService } from '@caslink/common-client/ng-app/common-broadcast/common-broadcast.service';
import { EventTypes } from '@caslink/common-client/src';
import { ThemeService } from '@caslink/common-client/ng-app/theme/ThemeService';
import { StateService } from '@uirouter/core';
import * as _ from 'lodash';
import { DeviceSettingsBaseComponent } from '../device-settings-base/device-settings-base.component';
import { ControlService } from '../control/control.service';
import { DashboardViewService } from '../shared/services/dashboard-view.service';
import { CurrentInfoService } from '../current-info/current-info.service';
import { ScheduleService } from '../schedule/schedule.service';
import { SystemSettingsService } from '../system-settings/system-settings.service';
import { DeviceService } from '../shared/services/device.service';
import { DeviceInformation } from '../shared/models/device-information';
import { DeviceModelService } from '../shared/services/device-model.service';
import { DynamicDeviceModelService } from '../device-model/dynamic-device-model.service';
import { GraphicCardData } from '../device-model/model/graphic-card-data';
import { TemplateChoiceDialogComponent } from '../template-choice-dialog/template-choice-dialog.component';
import { TemplateNameDialogComponent } from '../template-name-dialog/template-name-dialog.component';
import { CaalFileDialogComponent } from '../caal-file-dialog/caal-file-dialog.component';
import { IJobDocumentTreeNodeViewModel } from '../shared/models/nola-job-document-models';
import { UpdateConfirmationDialogComponent } from '../update-confirmation-dialog/update-confirmation-dialog.component';
import {
    DeviceUpdateAction,
    IControlModel,
    IControlSubRoute,
    IDeviceModel,
    IDisplayInfo,
} from '../shared/models/models';
import { CardBackgroundUpdateService } from './card-background-update.service';
import { Subscription } from 'rxjs';
import { DashboardDeviceSummary } from '../shared/models/dashboard-device-summary.interface';
import { DeviceStatusViewInfoService } from '../shared/services/device-status-view-info.service';
import { DeviceStatusType } from '../shared/models/device-status-type.interface';
import { UserPermissionService } from '@caslink/common-client/ng-app/userPermission/UserPermissionService';
import { DialogService } from '@caslink/common-client/ng-app/dialog-service/dialog.service';
import { NolaJobDocumentService } from '../nola-job-document/nola-job-document.service';
import { DashboardToastrConfirmationService } from '../shared/services/dashboard-toastr-confirmation.service';

@Component({
    selector: 'dl-card',
    styleUrls: ['./card.component.scss'],
    templateUrl: './card.component.html',
    providers: [CardBackgroundUpdateService],
})
export class CardComponent extends DeviceSettingsBaseComponent implements OnInit, OnDestroy {
    public static $name: string = 'card';

    // The overarching model, e.g. Ecpm03Model
    @Input() public section: IDeviceModel;
    @Input() public currentControl: IControlModel;
    @Input() public areaId: number;
    @Input() public currentRouteHardwareId: number;
    @Input() public locationId: number;
    @Input() public device: DashboardDeviceSummary;

    // use this to overcome weird shaping of parent of mat-card when handling click events if you need direct card click
    @Output() public onCardClicked = new EventEmitter();
    @ViewChild('cardInfo', {static: false}) public elementView: ElementRef;
    @ViewChild('card', {read: ElementRef, static: false}) public card: ElementRef;

    @HostBinding('style.min-width') get minWidth2() {
        return this.minWidth + 'px';
    }

    @HostBinding('style.min-height') get minHeight2() {
        return this.minHeight + 'px';
    }

    public isReady = false;
    public isEditMode = false;
    public form: FormGroup;
    public displayInfoForEdit: IDisplayInfo;
    public minHeight: number | null = null;
    public minWidth: number | null = null;

    private coolingEventActive = false;
    private heatingEventActive = false;

    templateApplied: boolean;

    private cardStatusUpdateSubscription: Subscription;
    private viewModelUpdateSubscription: Subscription;

    updateWarning: string;

    constructor(
        protected controlService: ControlService,
        private commonBroadcastService: CommonBroadcastService,
        private cardBackgroundChangeService: CardBackgroundUpdateService,
        private dashboardViewService: DashboardViewService,
        private currentInfoService: CurrentInfoService,
        private scheduleService: ScheduleService,
        private systemSettingsService: SystemSettingsService,
        private deviceService: DeviceService,
        private dialogService: DialogService,
        protected deviceModelService: DeviceModelService,
        protected $state: StateService,
        protected dialog: MatDialog,
        private dynamicDeviceModelService: DynamicDeviceModelService,
        private deviceStatusViewInfoService: DeviceStatusViewInfoService,
        private themeService: ThemeService,
        private userPermissionService: UserPermissionService,
        private nolaJobDocumentService: NolaJobDocumentService,
        protected dashboardToastrConfirmationService: DashboardToastrConfirmationService,
    ) {
        super($state, deviceModelService, controlService, dialog, dashboardToastrConfirmationService);
    }

    public ngOnInit() {
        this.cardBackgroundChangeService.backgroundChangeEvent.subscribe((data: GraphicCardData) => {
            if (!this.currentControl ||
                !this.currentControl.displayInfo ||
                !data ||
                !data.sensorId) {
                return;
            }
            if (this.cardBackgroundChangeService.modelHasSensorWithId(this.currentControl.displayInfo, data.sensorId)) {
                this.heatingEventActive = false;
                this.coolingEventActive = false;

                if (data.displayValue === 'Heating') {
                    this.heatingEventActive = true;
                } else if (data.displayValue === 'Cooling') {
                    this.coolingEventActive = true;
                } else if (data.displayValue === 'Heating Cooling') {
                    this.heatingEventActive = true;
                    this.coolingEventActive = true;
                }
            }
        });

        this.viewModelUpdateSubscription = this.commonBroadcastService.on<ViewModelUpdateInfo>(EventTypes.ViewModelUpdate)
            .subscribe(data => {
                this.updateViewModel(data);
            });

        this.cardStatusUpdateSubscription = this.commonBroadcastService.on<CardStatusUpdateInfo>(EventTypes.CardStatusUpdate)
            .subscribe(data => {
                this.updateStatus(data);
            });

        this.form = new FormGroup({});
        this.isReady = true;
    }

    ngOnDestroy(): void {
        if (this.cardStatusUpdateSubscription) {
            this.cardStatusUpdateSubscription.unsubscribe();
        }
        if (this.viewModelUpdateSubscription) {
            this.viewModelUpdateSubscription.unsubscribe();
        }
    }

    public selectTemplate() {

        const dialogRef = this.dialogService.showDialogView(TemplateChoiceDialogComponent, {
            height: '1000px',
            data: {
                category: this.getDeviceCategory(),
                templateType: 'ControlSummary',
                locationId: this.locationId
            }
        });

        dialogRef.afterClosed().toPromise().then((result: string) => {
            if (result != null) {

                this.form = new FormGroup({});

                this.isReady = false;

                this.dashboardViewService.applyControlSummaryTemplate(this.deviceInformation, this.hardwareItemId, result).then((node) => {
                    this.displayInfoForEdit = this.dynamicDeviceModelService.getDisplayInfo(node);
                    this.deviceInformation = this.deviceService.setupDeviceInfo(node);
                    this.originalDeviceInformation = _.cloneDeep(this.deviceInformation);

                    this.isReady = true;
                    this.templateApplied = true;
                });

            }
        });
    }

    public makeTemplate() {
        const hasChanges: boolean = !(!(this.isEditMode && this.form.touched && this.form.valid) && !this.templateApplied);

        const dialogRef = this.dialog.open(TemplateNameDialogComponent, {
            height: '250px',
            data: {
                template: '',
                fullsnapshot: !hasChanges,
                disabled: !hasChanges,
            },
        });

        dialogRef.afterClosed().toPromise().then((result: any) => {
            if (result != null) {

                this.dashboardViewService.saveControlSummaryTemplate(this.deviceInformation, this.hardwareItemId, result.template, result.fullsnapshot).then(() => {
                    // do nothing
                });

            }
        });
    }

    public visibleSubroutesPart1() {
        return this.visibleSubroutes().slice(0, this.maxButtonsInCardToFitOneRow() - (this.shouldEditButtonBeVisible() ? 1 : 0));
    }

    public visibleSubroutesPart2() {
        return this.visibleSubroutes().slice(this.maxButtonsInCardToFitOneRow() - (this.shouldEditButtonBeVisible() ? 1 : 0));
    }

    public coolActive() {
        return this.coolingEventActive;
    }

    public heatActive() {
        return this.heatingEventActive;
    }

    public enableEditMode() {
        this.isEditMode = true;
        this.isReady = false;

        this.setMinDimensions();
        this.loadCurrent();
    }

    public showControlsLink(menu = false): boolean {
        const displayInMenuCheck = this.maxButtonsInCardToFitOneRow() > this.lengthControls() ? !menu : menu;
        return this.device &&
            this.dashboardViewService.showControlsLink(this.device) &&
            !this.isEditMode &&
            displayInMenuCheck;
    }

    public get controlsLink(): object {
        const state = this.device.model.subroutes.currentInfoState.name;
        const params = {
            areaId: this.device.hardwareItem.areaId,
            hardwareItemId: this.device.hardwareItem.id,
            locationId: this.device.locationId,
            modelName: this.device.hardwareCategory,
        };

        return {state: state, params: params};
    }

    public showMonitoringLink(menu = false): boolean {
        const displayInMenuCheck = this.maxButtonsInCardToFitOneRow() > this.lengthMonitoring() ? !menu : menu;
        return this.device &&
            this.device.model &&
            this.device.hardwareItem.areaId > 0 &&
            !this.isEditMode &&
            displayInMenuCheck;
    }

    public get monitoringLink(): object {
        const state = monitoring.sensors;
        const params = {
            areaId: this.device.hardwareItem.areaId,
            hardwareItemId: this.device.hardwareItem.id,
            locationId: this.device.locationId,
        };

        return {state: state, params: params};
    }

    public showAnalyticsLink(menu = false): boolean {
        const displayInMenuCheck = this.maxButtonsInCardToFitOneRow() > this.lengthAnalytics() ? !menu : menu;
        return this.device &&
            this.device.resource != null &&
            !this.isEditMode &&
            displayInMenuCheck;
    }

    public get analyticsLink(): object {
        const state = 'analytics-device-resource';
        const params = {
            locationId: this.device.locationId,
            hardwareItemId: this.device.hardwareItem.id,
            resourceId: this.device.resource.id,
        };

        return {state: state, params: params};
    }

    get deviceInformationConverted() {
        return this.deviceInformation as DeviceInformation;
    }

    private getUpdateWarning(): string {
        let defaultWarning = 'This action may trigger a system reboot at the site.';
        return defaultWarning + (this.updateWarning ? ` ${this.updateWarning}.` : '') + ' Are you sure?';
    }

    public confirmUpdateDeviceInfoEx() {

        this.dashboardViewService.previewControlSummaryTemplate(this.deviceInformation, this.hardwareItemId, 'preview', false).then((preview) => {


            const dialogRef = this.dialog.open(UpdateConfirmationDialogComponent, {
                data: {
                    dialogConfirmOptions: {
                        title: 'SETTINGS',
                        message: this.getUpdateWarning(),
                        messageType: MessageType.Warning,
                    },
                    preview: preview,
                },
            });

            dialogRef.afterClosed().toPromise().then((result: string) => {
                if (result != null) {

                    this.saveChanges(result);

                }
            });


        });

    }

    public saveChangesEx() {

        this.userPermissionService.getHasAccess('Settings.NoteRequiredForControlUpdate')
            .then((hasAccess: boolean) => {
                if (hasAccess) {
                    this.confirmUpdateDeviceInfoEx();
                } else {
                    this.saveChanges(null);
                }
            });
    }

    public saveChanges(note: string) {
        this.isReady = false;
        this.updateDevice(() => {
            this.loadCurrent();
            this.isEditMode = false;
        }, DeviceUpdateAction.CONTROL_SUMMARY, note);
    }

    public getDeviceStatusType() {
        if (this.device) {
            return this.device.deviceStatusType;
        }
        if (this.currentControl && this.currentControl.controlSummaryViewModel) {
            return this.currentControl.controlSummaryViewModel.deviceStatusType;
        }
        return null;
    }

    public getDeviceCategory() {
        if (this.device) {
            return this.device.category;
        }
        if (this.currentControl && this.currentControl.controlSummaryViewModel) {
            return this.currentControl.controlSummaryViewModel.deviceCategory;
        }
        return null;
    }

    public shouldEditButtonBeVisible(menu = false) {
        const controlPermissionCheck = this.device ? this.device.hasControlPermission : true;
        const cardEditableCheck = this.currentControl && this.currentControl.displayInfo.isCardEditable;
        const displayInMenuCheck = this.maxButtonsInCardToFitOneRow() > 0 ? !menu : menu;
        return cardEditableCheck &&
            controlPermissionCheck &&
            !this.isEditMode &&
            displayInMenuCheck;
    }

    public getMinDimensions() {
        return !this.isReady && this.minHeight && this.minWidth ? {
            height: `${this.minHeight}px`,
            width: `${this.minWidth}px`,
        } : {};
    }

    public getState() {
        let state = this.deviceStatusViewInfoService.getState(this.getDeviceStatusType() as unknown as DeviceStatusType, true);
        return state ? state.statusDescription : '';
    }

    public isDarkTheme() {
        return this.themeService.getTheme() === 'casDark';
    }

    public isMenuVisible() {
        return (this.shouldEditButtonBeVisible(true) ? 1 : 0) +
            this.visibleSubroutesPart2().length +
            (this.showAnalyticsLink(true) ? 1 : 0) +
            (this.showMonitoringLink(true) ? 1 : 0) +
            (this.showControlsLink(true) ? 1 : 0) +
            ((this.shouldExportToCAALFileButtonBeVisible() && this.currentControl?.commands?.commisionerModelOperations?.isVisible) ? 1 : 0);
    }

    public setDeviceInfo = (deviceInfo: DeviceInformation) => {
        this.isLoaded = false;
        setTimeout(() => {
            this.deviceInformation = deviceInfo;
            this.displayInfoForEdit = this.dynamicDeviceModelService.getDisplayInfo(deviceInfo as unknown as CASLink.Device.Ui.ViewModel.DynamicControls.ControlSummaryViewModel);
            this.deviceInformation = this.deviceService.setupDeviceInfo(deviceInfo);
            this.isLoaded = true;
        });
    };

    public unfocusAll() {
        if (document.activeElement && document.activeElement['blur']) {
            (document.activeElement as HTMLInputElement).blur();
        }
    }

    public shouldExportToCAALFileButtonBeVisible() {
        return !this.isEditMode &&
            this.currentControl != null && this.currentControl.jobNumber != null;
    }

    public onExportToCAALFileClick() {

        const dialogRef = this.dialog.open(CaalFileDialogComponent, {
            data: {
                caption: 'Please select Caal file for export',
                hardwareItemId: this.currentControl.id,
                selectedNodeData: null,
                isForExport: true,
                exportSettings: false,
                exportSettingsAllowed: this.shouldEditButtonBeVisible(false),
                exportSchedule: false,
                exportScheduleAllowed: this.currentControl.commands.scheduling.isVisible,
                exportFactory: false,
                exportFactoryAllowed: this.currentControl.commands.systemInformation.isVisible,
                exportAdvanced: false,
                exportAdvancedAllowed: this.currentControl.commands.currentInformation.isVisible,

            },
        });

        dialogRef.afterClosed().toPromise().then((result: any) => {
            if (result != null && result.selectedNodeData != null) {
                const selectedNodeData: IJobDocumentTreeNodeViewModel = result.selectedNodeData;
                // do export

                let promiseChain = Promise.resolve();
                let documentId: number = selectedNodeData.documentId;
                this.isReady = false;

                this.nolaJobDocumentService.archiveJobDocument(this.hardwareItemId, documentId, 'Configuration File Archive').then(() => {

                    if (result.exportSettings) {

                        promiseChain = promiseChain.then(() => {

                            return this.dashboardViewService.getControlSummary(this.hardwareItemId).then((node) => {
                                return this.deviceService.setupDeviceInfo(node);
                            }).then(controlSummary => {
                                return this.dashboardViewService.exportControlSummaryToCaal(controlSummary, this.hardwareItemId, documentId).then((docid) => {
                                    documentId = docid;
                                    if (!result.exportAdvanced && !result.exportSchedule && !result.exportFactory) {
                                        this.isReady = true;
                                    }
                                });
                            });


                        });


                    }

                    if (result.exportAdvanced) {


                        promiseChain = promiseChain.then(() => {

                            return this.currentInfoService.getCurrentInformation(this.hardwareItemId).then((node) => {
                                return this.deviceService.setupDeviceInfo(node);
                            }).then(currentInformation => {
                                return this.currentInfoService.exportCurrentInformationToCaal(currentInformation, this.hardwareItemId, documentId).then((docid) => {
                                    documentId = docid;
                                    if (!result.exportSchedule && !result.exportFactory) {
                                        this.isReady = true;
                                    }
                                });
                            });
                        });
                    }

                    if (result.exportSchedule) {
                        promiseChain = promiseChain.then(() => {
                            return this.scheduleService.getScheduling(this.hardwareItemId).then((node) => {
                                return this.deviceService.setupDeviceInfo(node);
                            }).then(scheduleInformation => {
                                return this.scheduleService.exportSchedulingToCaal(scheduleInformation, this.hardwareItemId, documentId).then((docid) => {
                                    documentId = docid;
                                    if (!result.exportFactory) {
                                        this.isReady = true;
                                    }
                                });
                            });
                        });
                    }

                    if (result.exportFactory) {
                        promiseChain = promiseChain.then(() => {

                            return this.systemSettingsService.getDeviceInformation(this.hardwareItemId).then((node) => {
                                return this.deviceService.setupDeviceInfo(node);
                            }).then(systemInformation => {
                                return this.systemSettingsService.exportDeviceInformationToCaal(systemInformation, this.hardwareItemId, documentId).then((docid) => {
                                    documentId = docid;
                                    this.isReady = true;
                                });
                            });
                        });
                    }
                });
            }
        });
    }

    public onImportFromCAALFileClick() {
        const dialogRef = this.dialog.open(CaalFileDialogComponent, {
            data: {
                caption: 'Please select Caal file for import',
                hardwareItemId: this.deviceInformation.$deviceHierarchy.identifier.entityId,
                jobNumber: this.deviceInformation.$deviceHierarchy.jobNumber,
                selectedNodeData: null,
            },
        });

        dialogRef.afterClosed().toPromise().then((result: any) => {
            if (result != null && result.selectedNodeData != null) {
                const selectedNodeData: IJobDocumentTreeNodeViewModel = result.selectedNodeData;

                this.form = new FormGroup({});

                this.isReady = false;

                this.dashboardViewService.importCaalToControlSummary(this.deviceInformation, this.hardwareItemId, selectedNodeData.documentId).then((node) => {
                    this.displayInfoForEdit = this.dynamicDeviceModelService.getDisplayInfo(node);
                    this.deviceInformation = this.deviceService.setupDeviceInfo(node);
                    this.originalDeviceInformation = _.cloneDeep(this.deviceInformation);

                    this.isReady = true;
                });
            }
        });
    }

    cardClicked($event) {
        $event.preventDefault();
        this.onCardClicked.emit($event);
    }

    private static parsePadding(padding: string) {
        if (padding.includes('px')) {
            return parseFloat(padding.replace('px', ''));
        }
        return 0;
    }

    // Prevent the card from shrinking while loading by setting min width/height properties
    private setMinDimensions() {
        const elementRef = this.card;
        const elementStyles = window.getComputedStyle(elementRef.nativeElement);
        const paddingTop = CardComponent.parsePadding(elementStyles.paddingTop);
        const paddingBottom = CardComponent.parsePadding(elementStyles.paddingBottom);
        const paddingLeft = CardComponent.parsePadding(elementStyles.paddingLeft);
        const paddingRight = CardComponent.parsePadding(elementStyles.paddingRight);

        this.minHeight = elementRef.nativeElement.offsetHeight - paddingTop - paddingBottom;
        this.minWidth = elementRef.nativeElement.offsetWidth - paddingLeft - paddingRight;
    }

    private loadCurrent() {
        this.dashboardViewService.getControlSummary(this.hardwareItemId).then((node) => {
            this.displayInfoForEdit = this.dynamicDeviceModelService.getDisplayInfo(node);
            this.deviceInformation = this.deviceService.setupDeviceInfo(node);
            this.originalDeviceInformation = _.cloneDeep(this.deviceInformation);
        }).finally(() => {
            this.isReady = true;

            this.templateApplied = false;

        });
    }

    private updateStatus(data: CardStatusUpdateInfo) {
        if (this.currentControl?.id === data?.hardwareItemId) {
            if (this.section) {
                this.section.updateStatus(this.currentControl, data.status);
            } else if (this.device && this.device.model) {
                this.device.model.updateStatus(this.currentControl, data.status);
            }

            if (data.status === 'Received') {
                this.isReady = true;
            }
        }
    }

    private updateViewModel(data: ViewModelUpdateInfo) {

        const isConfirmation: boolean = (typeof data.viewModel === 'boolean');

        if (this.device && this.device.hardwareItem && this.device.hardwareItem.id === data.entityId) {
            if (!isConfirmation) {
                this.device.deviceStatusType = data.viewModel.deviceStatusType as number;
            }
        }

        // Some controls won't have controlSummaryViewModels
        if (!(this.currentControl && this.currentControl.controlSummaryViewModel)) {
            return;
        }

        const newViewModel = data.viewModel;
        const currentViewModel = this.currentControl.controlSummaryViewModel;

        /* The SignalR update from the server side sends over a full newViewModel for the ViewModelUpdate,
         * but only a boolean true for the ViewModelUpdateConfirmation. */
        if (data.entityId === currentViewModel.identifier.entityId) {
            if (isConfirmation) {
                // this needs to be re-examined TODO
                if (this.section) {
                    this.section.updateStatus(this.currentControl, 'Received');
                } else if (this.device && this.device.model) {
                    this.device.model.updateStatus(this.currentControl, 'Received');
                }
            } else {
                if (this.section) {
                    this.section.updateCard(this.currentControl, newViewModel);
                } else if (this.device && this.device.model) {
                    this.device.model.updateCard(this.currentControl, newViewModel);
                }
            }
        }
    }

    private visibleSubroutes() {
        let result: IControlSubRoute[] = [];

        if (!this.section || this.isEditMode) {
            return result;
        }

        // eslint-disable-next-line guard-for-in
        for (let key in this.section.subroutes) {
            let subroute = this.section.subroutes[key] as IControlSubRoute;

            if (subroute.isVisible(this.currentControl)) {
                result.push(subroute);
            }
        }

        return result;
    }

    private maxButtonsInCardToFitOneRow() {
        const DEFAULT_CNT = 4;
        if (this.elementView && this.elementView.nativeElement && this.elementView.nativeElement.offsetWidth) {
            const BUTTON_WIDTH = 100;
            const MORE_BTN_WIDTH = 40;
            return Math.floor(Math.max(this.elementView.nativeElement.offsetWidth - MORE_BTN_WIDTH, 0) / BUTTON_WIDTH);
        }
        return DEFAULT_CNT;
    }

    private lengthAnalytics() {
        return (this.shouldEditButtonBeVisible() ? 1 : 0) +
            this.visibleSubroutesPart1().length;
    }

    private lengthMonitoring() {
        return (this.shouldEditButtonBeVisible() ? 1 : 0) +
            this.visibleSubroutesPart1().length +
            (this.showAnalyticsLink() ? 1 : 0);
    }

    private lengthControls() {
        return (this.shouldEditButtonBeVisible() ? 1 : 0) +
            this.visibleSubroutesPart1().length +
            (this.showAnalyticsLink() ? 1 : 0) +
            (this.showMonitoringLink() ? 1 : 0);
    }
}
